/**
 * TrackWorkspace
 *
 * Track-First interface for track designers and owners: the cycle's tracks
 * in a rail, and the selected track's page beside it.
 *
 * The page says where the track is once (2026-11-03). One progress strip —
 * participants → pool → criteria → scales → values → sessions → published
 * result, read from the server's readiness check — is the navigation: each
 * step opens that step's work below it. Under the strip sits the page's one
 * call to action (trackStory), which knows where the ball is: before the first
 * toss nothing on the track has to be set, so the toss is the one thing.
 * Relationships, Lineage and Discussion are tabs beside the steps.
 *
 * Conditions-met model: the steps can be worked in any order.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  LayoutGrid,
  ChevronRight,
  ChevronDown,
  Search,
  Plus,
  Flag,
  Loader2,
} from 'lucide-react';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useItemPoolsStore } from '@/stores/prioritization/itemPoolsStore';
import { useCycleStore } from '@/stores/prioritization/degapProcessStore';
import { useCatchballLoopStore } from '@/stores/prioritization/catchballLoopStore';
import { BallHolderBanner } from '@/components/prioritization/catchball/BallHolderBanner';
import { useMobileExperience } from '@/mobile/featureFlag';
import {
  computeTrackReadiness,
  trackConditionsMet,
  allTrackDesignConditionsMet,
  selectActiveTrackLoop,
} from '@/lib/trackReadiness';
import { useCycleSessions } from '@/stores/prioritization/sessionStore';
import { useTrackParticipantsStore } from '@/stores/prioritization/trackParticipantsStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { getTrackCellLabel, getTrackOwnerUserIdFromMetadata, UNSCOPED_CATEGORY, UNSCOPED_ALTITUDE } from '@/types/prioritization/track';
import type { Track } from '@/types/prioritization/track';
import { TrackProgress } from '@/components/prioritization/tracks/TrackProgress';
import { useTrackSessionCounts, useTrackSummaryServerFacts } from '@/components/prioritization/tracks/useTrackSummaryFacts';
import { trackStory, type StoryBall, type StoryStop, type StoryStopId } from '@/lib/tracks/trackStory';
import { normalizeLoopState } from '@/types/prioritization/catchballLoop';
import { useStoryOverlayEnabled } from '@/features/story-overlay/hooks/useStoryOverlayEnabled';
import { useSystemUsers } from '@/hooks/useSystemUsers';
import type { TrackSummaryBasics } from '@/components/prioritization/tracks/trackSummaryRows';
import { formatDate } from '@/i18n/formatters';
import {
  parseCycleDesignerConstraints,
  DESIGNER_COLOR_METADATA_KEY,
} from '@/features/cycle-designer';
import {
  TrackParticipationTab,
  TrackPoolTab,
  TrackCriteriaTab,
  TrackValueRangesTab,
  TrackValuesTab,
  TrackProvenanceTab,
  TrackDiscussionTab,
  TrackSessionsTab,
} from './TrackDetail';
import { WorkflowPresetSummary } from '@/components/prioritization/cycle-config/WorkflowPresetSummary';
import { TrackItemScopeSection } from '@/components/prioritization/cycle-config/TrackItemScopeSection';
import { TrackReadinessSection } from '@/components/prioritization/cycle-config/TrackReadinessSection';
import { TrackSessionDefaultsSection } from '@/components/prioritization/cycle-config/TrackSessionDefaultsSection';
import { TrackRelationshipsTab } from './TrackRelationshipsTab';
import { useUserStore } from '@/stores/prioritization/userStore';
import { trackViewFromUrl, TRACK_SIDE_TABS, type TrackView, type TrackViewTarget } from '@/lib/tracks/workspaceTab';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { resolveSendTheBallHref } from '@/lib/catchballInboxUrls';
import { StructureVersionFinePrint } from '@/components/hierarchy/StructureVersionFinePrint';

// Readiness derivations for the rail live in @/lib/trackReadiness; the page's
// own steps and next thing in @/lib/tracks/trackStory.

/** The steps a person sets before a session can run; before the first toss they are optional. */
const SETUP_STEPS: StoryStopId[] = ['participants', 'pool', 'criteria', 'scales', 'values'];

const SIDE_TAB_LABELS: Record<(typeof TRACK_SIDE_TABS)[number], string> = {
  relationships: 'Relationships',
  lineage: 'Lineage',
  discussion: 'Discussion',
};

// ─────────────────────────────────────────────────────────────────────────────
// Published result step
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The last step: what is published for the track, in the strip's own words,
 * and the way to the track's result page (where the person who holds the ball
 * chooses it) and to the cycle's results.
 */
function PublishedStep({ stop, resultHref, cycleResultsHref }: { stop: StoryStop | null; resultHref: string; cycleResultsHref: string }) {
  const link = { fontFamily: 'var(--mono)', fontSize: '0.75rem', letterSpacing: '0.06em', color: 'var(--text-sec)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' } as const;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ padding: '14px', background: 'var(--bg-raised)', border: '1px solid var(--rule)', borderRadius: '2px' }}>
        <div style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-pri)' }}>
          {stop?.value || 'Checking…'}
        </div>
        {stop?.note && <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--text-sec)' }}>{stop.note}</p>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
        <Link to={resultHref} style={{ ...link, color: 'var(--text-pri)' }}>
          Open the track’s result
          <ChevronRight size={12} />
        </Link>
        <Link to={cycleResultsHref} style={link}>
          <Flag size={12} style={{ color: 'var(--mustard)' }} />
          Cycle results
          <ChevronRight size={12} />
        </Link>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Track Sidebar Item
// ─────────────────────────────────────────────────────────────────────────────

interface TrackNavItemProps {
  track: Track;
  cycleName?: string;
  isActive: boolean;
  pools: ReturnType<typeof useItemPoolsStore.getState>['itemPools'];
  sessionCount: number;
  designerColor?: string;
  onClick: () => void;
}

function TrackNavItem({ track, cycleName, isActive, pools, sessionCount, designerColor, onClick }: TrackNavItemProps) {
  const readiness = computeTrackReadiness(track, pools, sessionCount);
  const met = trackConditionsMet(readiness);
  const total = 4;
  const pct = (met / total) * 100;
  const allMet = allTrackDesignConditionsMet(readiness);
  const cellLabel = getTrackCellLabel(track);
  const isDefault =
    track.requiredItemCategory === UNSCOPED_CATEGORY ||
    track.requiredAltitudeLevel === UNSCOPED_ALTITUDE;

  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        padding: '10px 14px 10px 12px',
        background: isActive ? 'color-mix(in srgb, var(--mustard) 6%, transparent)' : 'transparent',
        borderLeft: isActive ? '2px solid var(--mustard)' : '2px solid transparent',
        borderTop: 'none',
        borderRight: 'none',
        borderBottom: '1px solid var(--rule)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
      }}
    >
      {/* Track name + status dot */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
          {designerColor && (
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: designerColor, flexShrink: 0 }} />
          )}
          <span
            style={{
              fontFamily: 'var(--sans)',
              fontSize: '0.78rem',
              fontWeight: isActive ? 500 : 400,
              color: isActive ? 'var(--text-pri)' : 'var(--text-sec)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {track.name}
          </span>
        </span>
        {/* Smaller than the 12px floor on purpose (owner, 2026-10-07): at 12px this badge was
            24px tall beside an 18.7px track name and read as the biggest thing on the row.
            It must never be taller than the name it sits beside. The extra pixel of padding on
            top centers the capitals in the box: with equal padding they sat 3.4px from the top
            stroke and 4.5px from the bottom one. */}
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: '0.65rem',
            lineHeight: 1.3,
            letterSpacing: '0.08em',
            padding: '2px 5px 1px',
            background: allMet ? 'color-mix(in srgb, var(--green) 12%, transparent)' : 'color-mix(in srgb, var(--slate) 20%, transparent)',
            // Words on the slate wash are --text-sec (--text-ter is 3.84:1 on this 20% wash).
            // Forest words are --green-text: forest in light, --text-ter in Night (5.38:1 on this wash).
            color: allMet ? 'var(--green-text)' : 'var(--text-sec)',
            border: `1px solid ${allMet ? 'var(--green)' : 'var(--rule-bright)'}`,
            borderRadius: '2px',
            flexShrink: 0,
          }}
        >
          {allMet ? 'READY' : 'DESIGN'}
        </span>
      </div>

      {/* Cell + cycle */}
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: '0.75rem',
          letterSpacing: '0.04em',
          color: 'var(--text-dim)',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          minWidth: 0,
        }}
      >
        {!isDefault && (
          <>
            <span title={cellLabel} style={{ color: 'var(--mustard)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cellLabel}</span>
            {cycleName && <span style={{ flexShrink: 0 }}>·</span>}
          </>
        )}
        {cycleName && <span title={cycleName} style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cycleName}</span>}
      </div>

      {/* Readiness bar */}
      <div
        style={{
          height: '2px',
          background: 'var(--rule)',
          borderRadius: '1px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: allMet ? 'var(--green)' : 'var(--mustard)',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main TrackWorkspace
// ─────────────────────────────────────────────────────────────────────────────

export function TrackWorkspace() {
  const { trackId: urlTrackId } = useParams<{ trackId?: string }>();
  const [searchParams] = useSearchParams();
  const filterCycleId = searchParams.get('cycleId');
  const fromDesigner = searchParams.get('from') === 'designer';
  const navigate = useNavigate();
  // On a phone the track list rail would take half the screen; the phone
  // frame's back button returns to the cycle's tracks instead.
  const isPhone = useMobileExperience();

  // Stores — array fallbacks are memoized so hook dependencies stay referentially stable
  const tracksRaw = useTrackStore(s => s.tracks);
  const tracks = useMemo(() => tracksRaw || [], [tracksRaw]);
  const isLoading = useTrackStore(s => s.isLoading);
  const fetchAllTracks = useTrackStore(s => s.fetchAllTracks);
  const allPoolsRaw = useItemPoolsStore(s => s.itemPools);
  const allPools = useMemo(() => allPoolsRaw || [], [allPoolsRaw]);
  const fetchPools = useItemPoolsStore(s => s.fetchFromSupabase);
  const cyclesRaw = useCycleStore(s => s.cycles);
  const cycles = useMemo(() => cyclesRaw || [], [cyclesRaw]);
  const fetchParticipants = useTrackParticipantsStore(s => s.fetchParticipantsForTrack);
  const allItemsRaw = useItemsStore(s => s.items);
  const allItems = useMemo(() => allItemsRaw || [], [allItemsRaw]);
  const catchballLoops = useCatchballLoopStore(s => s.loops);
  const fetchCatchballLoops = useCatchballLoopStore(s => s.fetchFromSupabase);

  // Sessions for each cycle — we'll fetch for the selected track's cycle
  const [sessionCycleId, setSessionCycleId] = useState<string | null>(null);
  const trackSessions = useCycleSessions(sessionCycleId ?? '');

  // Local state
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(urlTrackId ?? null);
  // What is open below the strip: a step, a side tab, or 'current' — the step
  // the next thing points at, settled once the facts are read.
  const [view, setView] = useState<TrackViewTarget>(() => trackViewFromUrl(searchParams.get('tab')) ?? 'current');
  // Left-rail cardinality controls (§1E zero/one/few/many): search + per-cycle collapse
  const [railQuery, setRailQuery] = useState('');
  const [collapsedCycles, setCollapsedCycles] = useState<Set<string>>(new Set());

  // Fetch on mount
  useEffect(() => {
    fetchAllTracks();
    fetchPools();
  }, [fetchAllTracks, fetchPools]);

  // Auto-select first track
  useEffect(() => {
    if (!selectedTrackId && tracks.length > 0) {
      setSelectedTrackId(tracks[0].id);
    }
  }, [tracks, selectedTrackId]);

  // Sync URL param — deliberately reacts to the URL only; adding selectedTrackId
  // would re-fire on local selection and fight the user.
  useEffect(() => {
    if (urlTrackId && urlTrackId !== selectedTrackId) {
      setSelectedTrackId(urlTrackId);
    }
  }, [urlTrackId]); // eslint-disable-line react-hooks/exhaustive-deps

  // When track changes, fetch its participants and session cycle
  useEffect(() => {
    if (!selectedTrackId) return;
    fetchParticipants(selectedTrackId);
    const track = tracks.find(t => t.id === selectedTrackId);
    if (track?.cycleId) {
      setSessionCycleId(track.cycleId);
    }
  }, [selectedTrackId, tracks, fetchParticipants]);

  // Derived
  const selectedTrack = useMemo(
    () => tracks.find(t => t.id === selectedTrackId) ?? null,
    [tracks, selectedTrackId]
  );

  const readiness = useMemo(() => {
    if (!selectedTrack) return null;
    const sessionCount = trackSessions.filter(s => s.trackId === selectedTrackId).length;
    return computeTrackReadiness(selectedTrack, allPools, sessionCount);
  }, [selectedTrack, allPools, trackSessions, selectedTrackId]);

  const selectedTrackSessions = useMemo(
    () => trackSessions.filter(s => s.trackId === selectedTrackId),
    [trackSessions, selectedTrackId],
  );

  // Catchball possession: fetch the selected track's cycle loops, then find the
  // track's live (non-closed) loop so the ball-holder banner can name the holder.
  useEffect(() => {
    if (selectedTrack?.cycleId) void fetchCatchballLoops(selectedTrack.cycleId);
  }, [selectedTrack?.cycleId, fetchCatchballLoops]);

  const activeLoop = useMemo(
    () => selectActiveTrackLoop(catchballLoops, selectedTrackId),
    [catchballLoops, selectedTrackId],
  );
  const activeLoopId = activeLoop?.id ?? null;

  // The track's pool — one read, the same row the Item pool step draws. The
  // strip's pool step takes its name and count from it and its state from the
  // server. (There used to be a fallback that counted every item matching the
  // track's category when the track had no pool, so an unscoped track read
  // "106 items in pool" beside "No pool yet".)
  const itemPool = allPools.find(p => p.trackId === selectedTrackId);
  const poolItemIds: string[] = itemPool?.priorityIds ?? [];

  // The strip and the one thing to do now: the server's readiness check and the
  // published result (the same reader as the track summary card), plus what this
  // page holds. With module `story_overlay` on, the next thing says why.
  const storyOverlayOn = useStoryOverlayEnabled();
  const { users: storyUsers } = useSystemUsers();
  const storyBasics = useMemo<TrackSummaryBasics | null>(() => {
    if (!selectedTrack) return null;
    const day = (d: Date | string | null | undefined) => (d ? formatDate(new Date(d), { day: 'numeric', month: 'short' }) : '');
    const ownerId = getTrackOwnerUserIdFromMetadata(selectedTrack.metadata);
    const owner = ownerId ? storyUsers.find((u) => u.id === ownerId) : undefined;
    const poolLock = itemPool as { poolLockedAt?: Date | string | null; pool_locked_at?: string | null; lockedAt?: Date | string | null } | undefined;
    return {
      trackId: selectedTrack.id,
      href: `/prioritization/tracks/${selectedTrack.id}`,
      ownerName: ownerId ? owner?.name || owner?.email || 'Someone not listed here' : null,
      cycleName: selectedTrack.cycleId ? (cycles.find((c) => c.id === selectedTrack.cycleId)?.name ?? null) : null,
      pool: itemPool
        ? { name: itemPool.name, itemCount: poolItemIds.length, lockedOn: day(poolLock?.poolLockedAt ?? poolLock?.pool_locked_at ?? poolLock?.lockedAt) || null }
        : null,
      // This page does not hold the criteria's names; the readiness check speaks for that stop.
      criteria: null,
      lockedOn: {
        participation: day(selectedTrack.participationLockedAt),
        criteria: day(selectedTrack.criteriaLockedAt),
        scales: day(selectedTrack.rangeSetLockedAt),
        values: day(selectedTrack.valuesConvergedAt),
      },
    };
  }, [selectedTrack, storyUsers, cycles, itemPool, poolItemIds.length]);

  // The rail is scoped to the cycle you're working in: an explicit ?cycleId=
  // wins, otherwise the selected track's own cycle. Configuring one track is
  // not the place to browse other cycles' tracks (owner ruling 2026-07-08).
  const railCycleId = filterCycleId ?? selectedTrack?.cycleId ?? null;

  // A track in no cycle takes no part in Catchball (glossary › Track, Unbound).
  const sendTheBall = useMemo(() => {
    if (!railCycleId || !selectedTrackId || !selectedTrack?.cycleId) return null;
    return {
      ...resolveSendTheBallHref(railCycleId, selectedTrackId, activeLoop, { fromDesigner }),
      cycleId: railCycleId,
      trackId: selectedTrackId,
    };
  }, [railCycleId, selectedTrackId, selectedTrack?.cycleId, activeLoop, fromDesigner]);

  const currentUserId = useUserStore((st) => st.userId);

  const ball = useMemo<StoryBall>(() => ({
    send: sendTheBall,
    loopState: activeLoop ? normalizeLoopState(activeLoop.state) : null,
    heldByOther:
      !!activeLoop && activeLoop.state === 'drafting' && !!activeLoop.ballHolderUserId && activeLoop.ballHolderUserId !== currentUserId,
  }), [sendTheBall, activeLoop, currentUserId]);

  // A lock or a count changed on this page: read the server's facts again so the strip follows.
  const poolLockedAt = (itemPool as { poolLockedAt?: unknown; pool_locked_at?: unknown } | undefined);
  const storyRefreshKey = selectedTrack
    ? [
        selectedTrack.participationLockedAt,
        selectedTrack.criteriaLockedAt,
        selectedTrack.rangeSetLockedAt,
        selectedTrack.valuesConvergedAt,
        selectedTrack.status,
        String(poolLockedAt?.poolLockedAt ?? poolLockedAt?.pool_locked_at ?? ''),
        itemPool?.id ?? '',
        poolItemIds.length,
        selectedTrackSessions.length,
      ].map((v) => (v instanceof Date ? v.toISOString() : String(v ?? ''))).join('|')
    : '';
  const storyServer = useTrackSummaryServerFacts(selectedTrackId ?? '', undefined, storyRefreshKey);
  const storySessions = useTrackSessionCounts(selectedTrackId ?? '');
  const story = useMemo(
    () => (storyBasics ? trackStory(storyBasics, storySessions, storyServer, ball) : null),
    [storyBasics, storySessions, storyServer, ball],
  );

  // 'current' settles on the step the next thing points at — or, when it points
  // off the page (the toss, the published answer), the first step not done —
  // and then holds, so finishing a step does not move the page under you.
  const currentStep: StoryStopId | null = story?.next
    ? story.next.stop ?? story.stops.find((st) => st.state !== 'done')?.id ?? 'published'
    : null;
  const openView: TrackView | null = view === 'current' ? currentStep : view;
  useEffect(() => {
    if (view === 'current' && currentStep) setView(currentStep);
  }, [view, currentStep]);

  const cycleTracks = useMemo(() => {
    const byCycle: Record<string, { cycleName: string; tracks: Track[] }> = {};
    const standalone: Track[] = [];

    for (const t of tracks) {
      if (railCycleId && t.cycleId !== railCycleId) continue;
      if (!t.cycleId) {
        if (!railCycleId) standalone.push(t);
        continue;
      }
      if (!byCycle[t.cycleId]) {
        const cycle = cycles.find(c => c.id === t.cycleId);
        byCycle[t.cycleId] = {
          cycleName: cycle?.name ?? 'Unknown cycle',
          tracks: [],
        };
      }
      byCycle[t.cycleId].tracks.push(t);
    }

    return { byCycle, standalone };
  }, [tracks, cycles, railCycleId]);


  // §1E: rail entries filtered by the search query (name match, case-insensitive).
  const railFiltered = useMemo(() => {
    const q = railQuery.trim().toLowerCase();
    if (!q) return cycleTracks;
    const byCycle: typeof cycleTracks.byCycle = {};
    for (const [cId, group] of Object.entries(cycleTracks.byCycle)) {
      const matched = group.tracks.filter(t => (t.name ?? '').toLowerCase().includes(q));
      if (matched.length > 0) byCycle[cId] = { ...group, tracks: matched };
    }
    return {
      byCycle,
      standalone: cycleTracks.standalone.filter(t => (t.name ?? '').toLowerCase().includes(q)),
    };
  }, [cycleTracks, railQuery]);

  const toggleCycleCollapsed = (cId: string) => {
    setCollapsedCycles(prev => {
      const next = new Set(prev);
      if (next.has(cId)) next.delete(cId); else next.add(cId);
      return next;
    });
  };

  const filteredCycle = filterCycleId ? cycles.find(c => c.id === filterCycleId) : null;

  function handleSelectTrack(id: string) {
    setSelectedTrackId(id);
    const qs = filterCycleId ? `?cycleId=${filterCycleId}${fromDesigner ? '&from=designer' : ''}` : '';
    navigate(`/prioritization/tracks/${id}${qs}`, { replace: true });
  }

  // Another track — from the rail or from the URL — opens on its own current
  // step, or on what ?tab= names.
  useEffect(() => {
    setView(trackViewFromUrl(searchParams.get('tab')) ?? 'current');
  }, [selectedTrackId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ?tab= opens the page on what the tab names — the same links TrackDetail
  // honours, old tab names landing on the step that holds them now. Reacts to
  // the URL only, like the track id sync above.
  const urlTab = searchParams.get('tab');
  useEffect(() => {
    const target = trackViewFromUrl(urlTab);
    if (target) setView(target);
  }, [urlTab]);

  // V-02 parity data: same derivations TrackDetail uses for its tab bodies
  const wsTrackPools = useMemo(
    () => allPools.filter((pl) => pl.trackId === selectedTrackId),
    [allPools, selectedTrackId],
  );
  const wsTrackItems = useMemo(() => {
    const ids = new Set<string>();
    wsTrackPools.forEach((pl) => pl.priorityIds?.forEach((id) => ids.add(id)));
    return allItems.filter((it) => ids.has(it.id));
  }, [wsTrackPools, allItems]);

  const cellLabel = selectedTrack ? getTrackCellLabel(selectedTrack) : null;
  const selectedCycle = selectedTrack?.cycleId
    ? cycles.find(c => c.id === selectedTrack.cycleId)
    : null;

  const cycleConstraints = parseCycleDesignerConstraints(filteredCycle ?? selectedCycle);

  // Before the first toss the setup steps are optional: what is set here the
  // level below starts from, and what is left open they set.
  const beforeToss = ball.send?.mode === 'compose';

  function trackDesignerColor(track: Track): string | undefined {
    const meta = track.metadata as Record<string, unknown> | null | undefined;
    const color = meta?.[DESIGNER_COLOR_METADATA_KEY];
    return typeof color === 'string' ? color : undefined;
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100%',
        background: 'var(--bg)',
        color: 'var(--text-pri)',
        fontFamily: 'var(--sans)',
        overflow: 'hidden',
      }}
    >
      {/* ── Sidebar ── */}
      {!isPhone && (
      <aside
        style={{
          width: '240px',
          flexShrink: 0,
          borderRight: '1px solid var(--rule)',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg)',
          overflow: 'hidden',
        }}
      >
        {/* Sidebar header */}
        <div
          style={{
            padding: '14px 14px 10px',
            borderBottom: '1px solid var(--rule)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <LayoutGrid size={13} style={{ color: 'var(--text-ter)' }} />
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: '0.75rem',
                letterSpacing: '0.16em',
                color: 'var(--text-ter)',
                textTransform: 'uppercase',
              }}
            >
              {railCycleId ? 'Cycle Tracks' : 'My Tracks'}
            </span>
          </div>
          {!cycleConstraints?.catchballPathConfigured && (
          <button
            type="button"
            title="Create new Track"
            onClick={() => navigate(railCycleId
              ? `/prioritization/tracks?cycleId=${railCycleId}&create=1`
              : '/prioritization/tracks?create=1')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-dim)',
              padding: '2px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Plus size={13} />
          </button>
          )}
        </div>

        {/* §1E "many": search appears once the rail holds more than a few tracks */}
        {tracks.length > 5 && (
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--rule)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Search size={12} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
            <input
              value={railQuery}
              onChange={e => setRailQuery(e.target.value)}
              placeholder="Filter tracks…"
              style={{
                flex: 1,
                minWidth: 0,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: 'var(--text-pri)',
                fontFamily: 'var(--sans)',
                fontSize: '0.75rem',
              }}
            />
            {railQuery && (
              <button
                type="button"
                onClick={() => setRailQuery('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', fontSize: '0.75rem', padding: 0 }}
              >
                ×
              </button>
            )}
          </div>
        )}

        {/* Track list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {isLoading && tracks.length === 0 ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '24px',
              }}
            >
              <Loader2 size={16} style={{ color: 'var(--text-dim)', animation: 'spin 1s linear infinite' }} />
            </div>
          ) : (
            <>
              {/* Cycle-grouped tracks — collapsible per cycle; search overrides collapse (§1E) */}
              {Object.entries(railFiltered.byCycle).map(([cId, group]) => {
                const collapsed = !railQuery && collapsedCycles.has(cId);
                return (
                  <div key={cId}>
                    <button
                      type="button"
                      onClick={() => toggleCycleCollapsed(cId)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '8px 14px 4px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'var(--mono)',
                        fontSize: '0.75rem',
                        letterSpacing: '0.12em',
                        color: 'var(--text-dim)',
                        textTransform: 'uppercase',
                        textAlign: 'left',
                      }}
                    >
                      {collapsed ? <ChevronRight size={9} /> : <ChevronDown size={9} />}
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.cycleName}</span>
                      <span style={{ flexShrink: 0 }}>{group.tracks.length}</span>
                    </button>
                    {!collapsed && group.tracks.map(t => (
                      <TrackNavItem
                        key={t.id}
                        track={t}
                        cycleName={undefined}
                        isActive={t.id === selectedTrackId}
                        pools={allPools}
                        sessionCount={trackSessions.filter(s => s.trackId === t.id).length}
                        designerColor={trackDesignerColor(t)}
                        onClick={() => handleSelectTrack(t.id)}
                      />
                    ))}
                  </div>
                );
              })}

              {/* Standalone tracks */}
              {railFiltered.standalone.length > 0 && (
                <div>
                  <div
                    style={{
                      padding: '8px 14px 4px',
                      fontFamily: 'var(--mono)',
                      fontSize: '0.75rem',
                      letterSpacing: '0.12em',
                      color: 'var(--text-dim)',
                      textTransform: 'uppercase',
                    }}
                  >
                    Standalone
                  </div>
                  {railFiltered.standalone.map(t => (
                    <TrackNavItem
                      key={t.id}
                      track={t}
                      isActive={t.id === selectedTrackId}
                      pools={allPools}
                      sessionCount={trackSessions.filter(s => s.trackId === t.id).length}
                      onClick={() => handleSelectTrack(t.id)}
                    />
                  ))}
                </div>
              )}

              {tracks.length === 0 && !isLoading && (
                <div
                  style={{
                    padding: '20px 14px',
                    fontFamily: 'var(--sans)',
                    fontSize: '0.76rem',
                    color: 'var(--text-dim)',
                  }}
                >
                  No Tracks yet.
                </div>
              )}
              {tracks.length > 0 &&
                railQuery &&
                Object.keys(railFiltered.byCycle).length === 0 &&
                railFiltered.standalone.length === 0 && (
                  <div
                    style={{
                      padding: '20px 14px',
                      fontFamily: 'var(--sans)',
                      fontSize: '0.76rem',
                      color: 'var(--text-dim)',
                    }}
                  >
                    No tracks match “{railQuery}”.
                  </div>
                )}
            </>
          )}
        </div>
      </aside>
      )}

      {/* ── Main content ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {fromDesigner && filteredCycle && (
          <div
            style={{
              padding: '10px 24px',
              background: 'var(--surface)',
              borderBottom: '1px solid var(--rule)',
              fontSize: '0.75rem',
              color: 'var(--text-sec)',
              flexShrink: 0,
            }}
          >
            <span style={{ fontFamily: 'var(--mono)', fontSize: '0.75rem', letterSpacing: '0.12em', color: 'var(--text-ter)', textTransform: 'uppercase', marginRight: 10 }}>
              From Cycle Designer
            </span>
            Configure each Track for <strong style={{ color: 'var(--text-pri)' }}>{filteredCycle.name}</strong>.
            {cycleConstraints?.simplifiedMode
              ? ' Expedited cycle — focus on pool and criteria; participation is preset from your ball paths.'
              : ''}
            {' '}
            <Link
              to={`/prioritization/cycles/${filteredCycle.id}`}
              style={{ color: 'var(--text-pri)', textDecoration: 'underline', textUnderlineOffset: '2px' }}
            >
              Cycle dashboard →
            </Link>
          </div>
        )}
        {selectedTrack && readiness ? (
          <>
            {/* Track header bar — one line, truncating the cell and cycle labels
                first. */}
            <div
              style={{
                padding: '8px 24px',
                minHeight: '52px',
                boxSizing: 'border-box',
                borderBottom: '1px solid var(--rule)',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                columnGap: '16px',
                rowGap: '8px',
                flexShrink: 0,
              }}
            >
              {/* Identity */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1 1 160px', minWidth: 0 }}>
                <LayoutGrid size={14} style={{ color: 'var(--text-ter)', flexShrink: 0 }} />
                <h1
                  style={{
                    fontFamily: 'var(--serif)',
                    fontSize: '1.1rem',
                    fontWeight: 400,
                    color: 'var(--text-pri)',
                    margin: 0,
                    flexShrink: 0,
                    maxWidth: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {selectedTrack.name}
                </h1>
                <StructureVersionFinePrint table="tracks" id={selectedTrack.id} mode="started" className="hidden min-w-0 truncate lg:block" />

                {cellLabel && cellLabel !== 'Default' && (
                  <span
                    title={cellLabel}
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: '0.75rem',
                      letterSpacing: '0.08em',
                      padding: '3px 8px',
                      // 5%, not 10%: gold words on a 10% gold wash are 4.26:1 in light; 5% is 4.59:1.
                      background: 'color-mix(in srgb, var(--mustard) 5%, transparent)',
                      color: 'var(--mustard)',
                      border: '1px solid color-mix(in srgb, var(--mustard) 25%, transparent)',
                      borderRadius: '2px',
                      flexShrink: 1,
                      minWidth: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {cellLabel}
                  </span>
                )}

                {/* The cycle name gives way first — it is also in the breadcrumb
                    above and in the rail's group header. */}
                {selectedCycle && !fromDesigner && (
                  <span
                    title={selectedCycle.name}
                    style={{
                      fontFamily: 'var(--sans)',
                      fontSize: '0.75rem',
                      color: 'var(--text-ter)',
                      flexShrink: 1000,
                      minWidth: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {selectedCycle.name}
                  </span>
                )}
                {/* Same name as the "From Cycle Designer" banner directly above,
                    so it gives way first too. The outer span does the clipping:
                    the pill's own padding cannot collapse, so ellipsis on the
                    pill would leave an empty box behind. */}
                {fromDesigner && filteredCycle && (
                  <span title={filteredCycle.name} style={{ display: 'flex', flexShrink: 1000, minWidth: 0, overflow: 'hidden' }}>
                    <span
                      style={{
                        fontFamily: 'var(--mono)',
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em',
                        padding: '3px 8px',
                        background: 'color-mix(in srgb, var(--mustard) 5%, transparent)',
                        color: 'var(--mustard)',
                        border: '1px solid color-mix(in srgb, var(--mustard) 25%, transparent)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {filteredCycle.name}
                    </span>
                  </span>
                )}
              </div>

              {/* The DESIGN / SESSIONS switch is gone: Sessions is a step on the strip below. */}
            </div>

            {/* Everything under the header scrolls together, so on a short window
                the step's work is not squeezed under the strip. */}
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {/* Ball-holder accountability banner — names who holds the ball for the
                track's live catchball loop (non-blocking; hidden when unclaimed). */}
            {activeLoopId && (
              <div style={{ padding: '10px 24px 0', flexShrink: 0 }}>
                <BallHolderBanner loopId={activeLoopId} />
              </div>
            )}

            {/* Where this track is — the page's navigation — and the one thing to do now. */}
            {story && (
              <div style={{ padding: '10px 24px 0', flexShrink: 0 }}>
                <TrackProgress
                  story={story}
                  cycleName={storyBasics?.cycleName ?? null}
                  view={openView}
                  onOpen={setView}
                  explain={storyOverlayOn}
                />
              </div>
            )}

            {/* The tabs that are not steps of the work. */}
            <div
              role="tablist"
              aria-label="More about this track"
              style={{
                display: 'flex',
                padding: '0 24px',
                marginTop: '6px',
                borderBottom: '1px solid var(--rule)',
                background: 'var(--bg)',
                flexShrink: 0,
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
            >
              {TRACK_SIDE_TABS.map(tab => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={openView === tab}
                  onClick={() => setView(tab)}
                  style={{
                    padding: '8px 12px',
                    fontFamily: 'var(--mono)',
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    color: openView === tab ? 'var(--text-pri)' : 'var(--text-sec)',
                    background: 'none',
                    border: 'none',
                    borderBottom: openView === tab ? '2px solid var(--mustard)' : '2px solid transparent',
                    cursor: 'pointer',
                  }}
                >
                  {SIDE_TAB_LABELS[tab]}
                </button>
              ))}
            </div>

            {/* What the open step or tab holds */}
            <div style={{ flex: 1 }}>
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {openView === null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-dim)', fontSize: '0.8rem' }}>
                    <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    Reading where this track is…
                  </div>
                )}

                {/* Folds in what the Overview's "Upstream defaults (optional)" bar and
                    "Optional default" banner said, on the step it is about. */}
                {beforeToss && openView && SETUP_STEPS.includes(openView as StoryStopId) && (
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-sec)', maxWidth: '68ch' }}>
                    <strong style={{ color: 'var(--text-pri)', fontWeight: 600 }}>Optional before the toss.</strong>{' '}
                    What you set here, the people you toss the ball to start from; what you leave open, they set.
                    A ranking session runs only once it is set.
                  </p>
                )}

                {/* The participation body carries the track owner too (the old Defaults tab drew it a second time). */}
                {openView === 'participants' && <TrackParticipationTab track={selectedTrack} cycle={selectedCycle ?? null} />}
                {openView === 'pool' && (
                  <>
                    {/* TrackPoolTab's `pools` prop must be every pool in the
                        workspace, not this track's: the component filters it
                        BOTH ways — `trackId === track.id` for this track's own
                        pool, and `trackId !== track.id` for the "Add from pool"
                        picker. Handing it the pre-filtered list made the second
                        filter empty by construction, so that picker always said
                        "No other pools with items". Its "Start ranking session" /
                        "Open ranking session" CTA needs the track's sessions. */}
                    <TrackPoolTab track={selectedTrack} items={wsTrackItems} pools={allPools} sessions={selectedTrackSessions} />
                    {/* Which items this track takes, and when an item is ready — from the old Defaults tab. */}
                    <TrackItemScopeSection track={selectedTrack} onAfterSave={() => {}} />
                    <TrackReadinessSection track={selectedTrack} cycle={selectedCycle ?? null} />
                  </>
                )}
                {openView === 'criteria' && <TrackCriteriaTab track={selectedTrack} />}
                {openView === 'scales' && <TrackValueRangesTab track={selectedTrack} />}
                {openView === 'values' && (
                  <TrackValuesTab
                    track={selectedTrack}
                    readinessFlags={{
                      participationLocked: readiness.participationLocked,
                      poolLocked: readiness.poolLocked,
                      criteriaLocked: readiness.criteriaLocked,
                      rangeSetLocked: !!selectedTrack.rangeSetLockedAt,
                    }}
                  />
                )}
                {openView === 'sessions' && (
                  <>
                    {/* Configures in place (inline session cards + Setup/Participants/Items/Criteria). */}
                    <TrackSessionsTab
                      track={selectedTrack}
                      cycle={selectedCycle ?? null}
                      sessions={selectedTrackSessions}
                      conditionsSummary={null}
                    />
                    {/* How this track's sessions start — from the old Defaults tab. */}
                    <WorkflowPresetSummary cycle={selectedCycle ?? null} track={selectedTrack} />
                    <TrackSessionDefaultsSection track={selectedTrack} cycle={selectedCycle ?? null} />
                  </>
                )}
                {openView === 'published' && (
                  <PublishedStep
                    stop={story?.stops.find((st) => st.id === 'published') ?? null}
                    resultHref={`/prioritization/tracks/${selectedTrack.id}/result`}
                    cycleResultsHref={selectedTrack.cycleId ? `/prioritization/cycles/${selectedTrack.cycleId}/results` : '/prioritization/reports'}
                  />
                )}
                {openView === 'relationships' && (
                  <TrackRelationshipsTab
                    track={selectedTrack}
                    poolItems={wsTrackItems}
                    trackPool={wsTrackPools.find((pl) => pl.trackId === selectedTrack.id)}
                  />
                )}
                {openView === 'lineage' && <TrackProvenanceTab trackId={selectedTrack.id} />}
                {openView === 'discussion' && (
                  <TrackDiscussionTab
                    trackId={selectedTrack.id}
                    workspaceId={getCurrentWorkspaceId() ?? ''}
                  />
                )}
              </div>
            </div>
            </div>
          </>
        ) : (
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              color: 'var(--text-dim)',
            }}
          >
            {isLoading ? (
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <>
                <LayoutGrid size={28} style={{ opacity: 0.3 }} />
                <p style={{ fontFamily: 'var(--sans)', fontSize: '0.82rem', margin: 0 }}>
                  Select a track to get started
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
